import { Userpilot } from 'userpilot'

/*
	Facade over the Userpilot SDK.

	Library: https://docs.userpilot.com/developer/installation/web
	Package: https://www.npmjs.com/package/userpilot

	Usage:
		import { UserpilotService } from '@/models/utils/UserpilotUtils.js';

		UserpilotService.initialize();                      //Once, before createApp
		UserpilotService.identify('42', { company: {...} }); //On authentication
		UserpilotService.reload();                          //On every confirmed navigation
		UserpilotService.track('documents_add', { ... });    //On a notable user action
		UserpilotService.clean();                           //On logout

	Notes:
		Userpilot ignores reload() for a visitor it has not identified, so reload() is gated
		on isIdentified() here rather than at each call site.

		identify() is only called by the login views, so it has not run after a browser
		refresh of a live session. router.afterEach re-identifies when isIdentified() is
		false, which is why the flag being page-load-scoped is safe.

		reload() reports the current URL to Userpilot. 'pageview: pathonly' keeps the query
		string out of that report - /auth/reset-password and /auth/register carry a live
		token and the user's email as query params, and those must not reach a third party.
 */


const USERPILOT_TOKEN = import.meta.env.VITE_USERPILOT_TOKEN

/*
	pageview: 'pathonly' strips the query string from the URL reported to Userpilot. See
	the note above.

	auto_capture governs AUTOCAPTURE ONLY - the element text Userpilot attaches to the click
    and input events it records automatically. It has NO effect on Session Replay. Only the
    SDK's autocapture module reads window.userpilotSettings.auto_capture; the Session Replay
    module takes its privacy rules from the socket 'configuration' payload instead, which
    carries the lists configured in Userpilot's Data Capture & Privacy settings and nothing
    else. There is no code-side hook for Session Replay masking.

	So these lists must be kept identical to the dashboard's Mask elements / Exclude elements
	lists - changing one means changing the other. They are declared here as well so the
	autocapture rules stay in version control and so the intended selectors are discoverable
	from the code. Userpilot's built-in masking of password and credit-card fields applies on
	top of both and cannot be removed.

	Both lists are CSS selectors - Userpilot joins each list with commas and hands it to rrweb
	as maskTextSelector and blockSelector, then matches with element.matches()/closest(). A
	bare class name without the leading dot is a valid *type* selector, which is why the custom
	element and the class are listed separately below.

	Masking covers text nodes only. An attribute that repeats the text - a :title tooltip, say -
	is still captured verbatim, and the dashboard's Exclude attributes list does not reach
	Session Replay either. Keep sensitive values out of attributes.

		.userpilot-mask     Text of the element and its descendants is replaced in the replay.
		.userpilot-exclude  The element is not captured at all; it replays as a blank
		                    placeholder box, so it will visibly hole out the layout.
		userpilot-mask		Mask anything wrapped in <userpilot-mask></userpilot-mask> tags.
		userpilot-exclude	Exclude anything wrapped in <userpilot-exclude></userpilot-exclude> tags.

	Note: Claude flagged that <span :title="some_value"></span> would leak the text. When reviewing the
			session replay the title tooltip is not displayed.
 */
const USERPILOT_SETTINGS = {
	pageview: 'pathonly',
	auto_capture: {
		masked_elements: [
			'userpilot-mask',
			'.userpilot-mask',
			'input[type="email"]',
			'input[type="password"]'
		],
		excluded_elements: [
			'userpilot-exclude',
			'.userpilot-exclude'
		]
	}
}

/**
 * True once identify() has run in this page load. Reset by clean().
 * @type {boolean}
 */
let identified_eh = false


/**
 * Userpilot user-tracking utilities
 */
export const UserpilotService =
{
	/**
	 * Returns true if Userpilot has been told who the current user is. Userpilot ignores
	 * reload() until this is true.
	 * @returns {boolean}
	 */
	isIdentified()
	{
		return identified_eh;
	},

	/**
	 * Loads the Userpilot SDK. Call once, before the app is created.
	 */
	initialize()
	{
		Userpilot.initialize(USERPILOT_TOKEN, USERPILOT_SETTINGS);
	},

	/**
	 * Associates all subsequent activity with a user.
	 * @param {string} p_user_id Userpilot's unique id for the user
	 * @param {Object} p_traits User and company properties
	 */
	identify(p_user_id, p_traits)
	{
		Userpilot.clean();//Reset when changing users
		Userpilot.identify(p_user_id, p_traits);
		identified_eh = true;
	},

	/**
	 * Re-evaluates Userpilot content against the current URL. No-op until the user has
	 * been identified, because Userpilot would ignore it anyway.
	 */
	reload()
	{
		if (!identified_eh) return;

		Userpilot.reload();
	},

	/**
	 * Records a custom event.
	 * @param {string} p_event_name Dotted event name, e.g. 'documents.add'
	 * @param {Object} [p_properties] Event metadata
	 */
	track(p_event_name, p_properties)
	{
		Userpilot.track(p_event_name, p_properties);
	},

	/**
	 * Forgets the current user. Call on logout.
	 */
	clean()
	{
		Userpilot.clean();
		identified_eh = false;
	}
}
